/**
 * StandaloneDiningDashboard - Cafeteria/Restaurant management without events
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  UtensilsCrossed, ShoppingCart, Package, Users, Plus, 
  DollarSign, Clock, AlertTriangle, ChefHat, Star,
  Coffee, Settings, TrendingUp, Grid3X3, BookOpen, ArrowLeft,
  CreditCard
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useOpsRole } from '@/hooks/useOpsRole';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sub-components
import MenuManagement from './dining/MenuManagement';
import OrdersManagement from './dining/OrdersManagement';
import StockManagement from './dining/StockManagement';
import TablesManagement from './dining/TablesManagement';
import DiningSettings from './dining/DiningSettings';
import RecipeManager from './dining/RecipeManager';
import DiningStaffManager from './dining/DiningStaffManager';
import { getToken } from '@/lib/authToken';

const StandaloneDiningDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  // Role-gating — hide management tabs (Staff, Stock, Settings) for staff
  // without dining.team / dining.menu permissions. Backend still enforces.
  const { can, isSuper, loading: roleLoading } = useOpsRole('dining');

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch dining stats
  const { data: stats = {}, isLoading: statsLoading } = useQuery({
    queryKey: ['dining-stats'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/dining/stats`, { headers });
      if (!res.ok) return {};
      return res.json();
    },
    refetchInterval: 15000
  });

  // Fetch pending orders
  const { data: pendingOrders = [] } = useQuery({
    queryKey: ['pending-orders'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/dining/orders?status=pending`, { headers });
      if (!res.ok) return [];
      return res.json();
    },
    refetchInterval: 10000
  });

  // Fetch preparing orders
  const { data: preparingOrders = [] } = useQuery({
    queryKey: ['preparing-orders'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/dining/orders?status=preparing`, { headers });
      if (!res.ok) return [];
      return res.json();
    },
    refetchInterval: 10000
  });

  // Fetch today's specials
  const { data: specials = [] } = useQuery({
    queryKey: ['today-specials'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/dining/specials`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="standalone-dining-dashboard">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <UtensilsCrossed className="h-8 w-8 text-orange-600" />
            <h1 className="text-2xl font-bold text-slate-900">Dining Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline"
              onClick={() => navigate('/pos')}
              data-testid="open-pos-btn"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Open POS
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/admin')}
              data-testid="back-to-admin-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Admin
            </Button>
          </div>
        </div>
        <p className="text-slate-600">Manage your cafeteria, restaurant, and kitchen</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('orders')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Today's Orders</p>
                <p className="text-2xl font-bold">{stats.today_orders || 0}</p>
              </div>
              <ShoppingCart className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('orders')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pending_orders || 0}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('orders')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Preparing</p>
                <p className="text-2xl font-bold text-orange-600">{stats.preparing_orders || 0}</p>
              </div>
              <ChefHat className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Today's Revenue</p>
                <p className="text-2xl font-bold text-green-600">${(stats.today_revenue || 0).toFixed(2)}</p>
              </div>
              <DollarSign className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('tables')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Tables</p>
                <p className="text-2xl font-bold">{stats.occupied_tables || 0}/{stats.total_tables || 0}</p>
              </div>
              <Grid3X3 className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('stock')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Low Stock</p>
                <p className={`text-2xl font-bold ${stats.low_stock_count > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {stats.low_stock_count || 0}
                </p>
              </div>
              <AlertTriangle className={`h-8 w-8 ${stats.low_stock_count > 0 ? 'text-red-500' : 'text-green-500'}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-8 w-full max-w-4xl" data-testid="dining-tabs">
          <TabsTrigger value="dashboard" className="flex items-center gap-2" data-testid="dining-tab-dashboard">
            <TrendingUp className="h-4 w-4" />
            <span className="hidden sm:inline">Overview</span>
          </TabsTrigger>
          <TabsTrigger value="orders" className="flex items-center gap-2" data-testid="dining-tab-orders">
            <ShoppingCart className="h-4 w-4" />
            <span className="hidden sm:inline">Orders</span>
          </TabsTrigger>
          {(isSuper || can('dining.menu')) && (
            <TabsTrigger value="menu" className="flex items-center gap-2" data-testid="dining-tab-menu">
              <UtensilsCrossed className="h-4 w-4" />
              <span className="hidden sm:inline">Menu</span>
            </TabsTrigger>
          )}
          {(isSuper || can('dining.menu')) && (
            <TabsTrigger value="recipes" className="flex items-center gap-2" data-testid="dining-tab-recipes">
              <BookOpen className="h-4 w-4" />
              <span className="hidden sm:inline">Recipes</span>
            </TabsTrigger>
          )}
          <TabsTrigger value="tables" className="flex items-center gap-2" data-testid="dining-tab-tables">
            <Grid3X3 className="h-4 w-4" />
            <span className="hidden sm:inline">Tables</span>
          </TabsTrigger>
          {(isSuper || can('dining.manage') || roleLoading) && (
            <TabsTrigger value="stock" className="flex items-center gap-2" data-testid="dining-tab-stock">
              <Package className="h-4 w-4" />
              <span className="hidden sm:inline">Stock</span>
            </TabsTrigger>
          )}
          {(isSuper || can('dining.team') || roleLoading) && (
            <TabsTrigger value="staff" className="flex items-center gap-2" data-testid="dining-tab-staff">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Staff</span>
            </TabsTrigger>
          )}
          {(isSuper || can('dining.manage') || roleLoading) && (
            <TabsTrigger value="settings" className="flex items-center gap-2" data-testid="dining-tab-settings">
              <Settings className="h-4 w-4" />
              <span className="hidden sm:inline">Settings</span>
            </TabsTrigger>
          )}
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Quick Actions */}
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => setActiveTab('orders')} data-testid="new-order-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Order
            </Button>
            <Button variant="outline" onClick={() => setActiveTab('menu')}>
              <UtensilsCrossed className="h-4 w-4 mr-2" />
              View Menu
            </Button>
            <Button variant="outline" onClick={() => setActiveTab('stock')}>
              <Package className="h-4 w-4 mr-2" />
              Check Stock
            </Button>
            <div className="border-l pl-3 ml-2 flex gap-2">
              <Button 
                variant="secondary" 
                className="bg-orange-100 hover:bg-orange-200 text-orange-800"
                onClick={() => window.open('/pos', '_blank')}
                data-testid="open-pos-btn"
              >
                <ShoppingCart className="h-4 w-4 mr-2" />
                Open POS
              </Button>
              <Button 
                variant="secondary" 
                className="bg-slate-800 hover:bg-slate-900 text-white"
                onClick={() => window.open('/kitchen', '_blank')}
                data-testid="open-kds-btn"
              >
                <ChefHat className="h-4 w-4 mr-2" />
                Kitchen Display
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Active Orders */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Clock className="h-5 w-5 text-yellow-600" />
                  Active Orders ({pendingOrders.length + preparingOrders.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {pendingOrders.length === 0 && preparingOrders.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No active orders</p>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {[...pendingOrders, ...preparingOrders].slice(0, 5).map((order) => (
                      <div key={order.id} className={`flex items-center justify-between p-3 rounded-lg ${
                        order.status === 'pending' ? 'bg-yellow-50' : 'bg-orange-50'
                      }`}>
                        <div>
                          <p className="font-medium">{order.order_number}</p>
                          <p className="text-sm text-gray-500">
                            {order.order_type === 'dine_in' ? `Table ${order.table_id}` : order.order_type}
                            {' • '}{order.items?.length || 0} items
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge variant={order.status === 'pending' ? 'warning' : 'default'}>
                            {order.status}
                          </Badge>
                          <p className="text-sm font-bold mt-1">${order.total?.toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Today's Specials */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Star className="h-5 w-5 text-yellow-500" />
                  Today's Specials ({specials.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {specials.length === 0 ? (
                  <div className="text-center py-4">
                    <Star className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                    <p className="text-gray-500">No specials today</p>
                    <Button variant="link" onClick={() => setActiveTab('settings')}>
                      Add a special
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {specials.map((special) => (
                      <div key={special.id} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                        <div>
                          <p className="font-medium">{special.menu_item_name}</p>
                          {special.description && (
                            <p className="text-sm text-gray-500">{special.description}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-sm line-through text-gray-400">${special.original_price?.toFixed(2)}</p>
                          <p className="text-lg font-bold text-green-600">${special.special_price?.toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Menu & Stock Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('menu')}>
              <CardContent className="p-4 text-center">
                <UtensilsCrossed className="h-8 w-8 mx-auto mb-2 text-orange-500" />
                <p className="text-2xl font-bold">{stats.total_menu_items || 0}</p>
                <p className="text-sm text-gray-500">Menu Items</p>
                <p className="text-xs text-green-600">{stats.available_items || 0} available</p>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('settings')}>
              <CardContent className="p-4 text-center">
                <Coffee className="h-8 w-8 mx-auto mb-2 text-brown-500" />
                <p className="text-2xl font-bold">{stats.total_categories || 0}</p>
                <p className="text-sm text-gray-500">Categories</p>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('tables')}>
              <CardContent className="p-4 text-center">
                <Grid3X3 className="h-8 w-8 mx-auto mb-2 text-purple-500" />
                <p className="text-2xl font-bold">{stats.available_tables || 0}</p>
                <p className="text-sm text-gray-500">Available Tables</p>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md" onClick={() => setActiveTab('settings')}>
              <CardContent className="p-4 text-center">
                <Star className="h-8 w-8 mx-auto mb-2 text-yellow-500" />
                <p className="text-2xl font-bold">{stats.today_specials || 0}</p>
                <p className="text-sm text-gray-500">Today's Specials</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders">
          <OrdersManagement />
        </TabsContent>

        {/* Menu Tab */}
        <TabsContent value="menu">
          <MenuManagement />
        </TabsContent>

        {/* Recipes Tab */}
        <TabsContent value="recipes">
          <RecipeManager />
        </TabsContent>

        {/* Tables Tab */}
        <TabsContent value="tables">
          <TablesManagement />
        </TabsContent>

        {/* Stock Tab */}
        <TabsContent value="stock">
          <StockManagement />
        </TabsContent>

        {/* Staff Tab */}
        <TabsContent value="staff">
          <DiningStaffManager />
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <DiningSettings />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StandaloneDiningDashboard;
